import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Users, 
  Building2, 
  FileText, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  ArrowRight,
  TrendingUp
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [recentInterventions, setRecentInterventions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, interventionsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/users`),
        axios.get(`${API}/interventions`)
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setRecentInterventions(interventionsRes.data.slice(0, 5));
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { styles: 'bg-orange-100 text-orange-700 border-orange-200', label: 'En attente' },
      validated: { styles: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: 'Validée' },
      rejected: { styles: 'bg-red-100 text-red-700 border-red-200', label: 'Rejetée' }
    };
    const { styles, label } = config[status] || {
      styles: 'bg-slate-100 text-slate-700 border-slate-200',
      label: 'Inconnu'
    };
    return <Badge variant="outline" className={styles}>{label}</Badge>;
  };

  const getRoleBadge = (role) => {
    const config = {
      admin: { styles: 'bg-purple-100 text-purple-700 border-purple-200', label: 'Admin' },
      consultant: { styles: 'bg-blue-100 text-blue-700 border-blue-200', label: 'Consultant' },
      sme: { styles: 'bg-slate-100 text-slate-700 border-slate-200', label: 'PME' }
    };
    const { styles, label } = config[role] || {
      styles: 'bg-slate-100 text-slate-700 border-slate-200',
      label: 'Inconnu'
    };
    return <Badge variant="outline" className={styles}>{label}</Badge>;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-slate-900 border-t-transparent rounded-full"></div>
        </div>
      </Layout>
    );
  }

  const consultantCount = users.filter(u => u.role === 'consultant').length;
  const smeCount = users.filter(u => u.role === 'sme').length;

  return (
    <Layout>
      <div className="space-y-8 fade-in" data-testid="admin-dashboard">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Tableau de bord Admin</h1>
          <p className="text-slate-500 mt-1">Vue d'ensemble de la plateforme</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="border-slate-200 shadow-sm" data-testid="stat-users">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 font-medium uppercase tracking-wider">Utilisateurs</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{users.length}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    {consultantCount} consultants, {smeCount} PME
                  </p>
                </div>
                <div className="p-3 bg-slate-100 rounded-lg">
                  <Users className="w-6 h-6 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm" data-testid="stat-interventions">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 font-medium uppercase tracking-wider">Interventions</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.total_interventions || 0}</p>
                </div>
                <div className="p-3 bg-slate-100 rounded-lg">
                  <FileText className="w-6 h-6 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm" data-testid="stat-pending">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-600 font-medium uppercase tracking-wider">En attente</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.pending_validations || 0}</p>
                </div>
                <div className="p-3 bg-orange-100 rounded-lg">
                  <Clock className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm" data-testid="stat-hours">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 font-medium uppercase tracking-wider">Heures totales</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.total_hours?.toFixed(1) || 0}</p>
                </div>
                <div className="p-3 bg-slate-100 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Users */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <div>
                <CardTitle className="text-xl font-semibold text-slate-900">Utilisateurs récents</CardTitle>
                <CardDescription className="text-slate-500">Dernières inscriptions</CardDescription>
              </div>
              <Button variant="ghost" asChild className="text-slate-600 hover:text-slate-900">
                <Link to="/admin/users" data-testid="view-all-users">
                  Voir tout <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              {users.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Aucun utilisateur</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {users.slice(0, 5).map((user) => (
                    <div 
                      key={user.id} 
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                      data-testid={`user-${user.id}`}
                    >
                      <div>
                        <p className="font-medium text-slate-900">{user.name}</p>
                        <p className="text-sm text-slate-500">{user.email}</p>
                      </div>
                      {getRoleBadge(user.role)}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Interventions */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <div>
                <CardTitle className="text-xl font-semibold text-slate-900">Interventions récentes</CardTitle>
                <CardDescription className="text-slate-500">Dernières activités</CardDescription>
              </div>
              <Button variant="ghost" asChild className="text-slate-600 hover:text-slate-900">
                <Link to="/admin/interventions" data-testid="view-all-interventions">
                  Voir tout <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              {recentInterventions.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Aucune intervention</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentInterventions.map((intervention) => (
                    <Link 
                      key={intervention.id} 
                      to={`/admin/interventions/${intervention.id}`}
                      className="block"
                      data-testid={`intervention-${intervention.id}`}
                    >
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                        <div>
                          <p className="font-medium text-slate-900">{intervention.sme_company_name}</p>
                          <p className="text-sm text-slate-500">
                            {intervention.intervention_type} par {intervention.consultant_name}
                          </p>
                        </div>
                        {getStatusBadge(intervention.status)}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};
